import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, Loader2, PencilLine, type LucideIcon } from 'lucide-react';
import { appendPhrase, isQuickPickField } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn, timeOnly } from '@/lib/utils';
import { Field, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { NoteTemplatesMenu } from '../NoteTemplatesMenu';
import { noteText, type Encounter } from './types';
import type { NoteFieldDef } from './sections';
import { EmrPanel } from './EmrPanel';
import { QuickPickChips, useQuickPicks } from './QuickPicks';

const AUTOSAVE_MS = 2_500;

export function useEncounterNotesWriter(encounterId: string) {
  const queryClient = useQueryClient();
  return async (patch: Record<string, unknown>) => {
    await put(`/encounters/${encounterId}`, { notes: patch });
    queryClient.setQueryData<Encounter>(['encounter', encounterId], (old) => (old ? { ...old, notes: { ...old.notes, ...patch } } : old));
  };
}

export function SaveState({ readOnly, saving, dirty, savedAt }: { readOnly: boolean; saving: boolean; dirty: boolean; savedAt: Date | null }) {
  if (readOnly) return <span className="text-xs text-muted-foreground">{tr('opd', 'closed_read_only')}</span>;
  const Icon = saving ? Loader2 : dirty ? PencilLine : CircleCheck;
  const text = saving
    ? tr('opd', 'saving')
    : dirty
      ? tr('opd', 'unsaved_changes')
      : savedAt
        ? tr('opd', 'saved_x', { savedAt: timeOnly(savedAt.toISOString()) })
        : tr('opd', 'autosaves_as_you_type');
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs', dirty ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground')}>
      <Icon className={cn('h-3.5 w-3.5', saving && 'animate-spin')} aria-hidden />
      {text}
    </span>
  );
}

export function NotePanel({
  encounter,
  fields,
  readOnly,
  withTemplates,
  icon,
  title,
  description,
}: {
  encounter: Encounter;
  fields: NoteFieldDef[];
  readOnly: boolean;
  withTemplates?: boolean;
  icon: LucideIcon;
  title: string;
  description?: string;
}) {
  const toast = useToast();
  const write = useEncounterNotesWriter(encounter.id);
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(fields.map((field) => [field.key, noteText(encounter, field.key)])));
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const latest = useRef(values);
  const pending = useRef(false);
  const writeRef = useRef(write);
  latest.current = values;
  pending.current = dirty && !readOnly;
  writeRef.current = write;

  const save = useMutation({
    mutationFn: (patch: Record<string, string>) => write(patch),
    onSuccess: (_, patch) => {
      if (patch === latest.current) setDirty(false);
      setSavedAt(new Date());
    },
    onError: (error) => toast.error(tr('opd', 'the_note_could_not_be_saved'), describeError(error)),
  });
  const mutateRef = useRef(save.mutate);
  mutateRef.current = save.mutate;
  const picks = useQuickPicks(!readOnly, encounter.id);
  const fieldKeys = fields.map((field) => field.key).join('|');
  const pickers = useMemo(
    () =>
      Object.fromEntries(
        fieldKeys.split('|').map((key) => [
          key,
          (phrase: string) => {
            setValues((current) => ({ ...current, [key]: appendPhrase(current[key] ?? '', phrase) }));
            setDirty(true);
          },
        ]),
      ) as Record<string, (phrase: string) => void>,
    [fieldKeys],
  );

  useEffect(() => {
    if (!dirty || readOnly) return undefined;
    const timer = setTimeout(() => mutateRef.current(latest.current), AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [values, dirty, readOnly]);

  useEffect(
    () => () => {
      if (pending.current) void writeRef.current(latest.current).catch(() => undefined);
    },
    [],
  );

  const status = <SaveState readOnly={readOnly} saving={save.isPending} dirty={dirty} savedAt={savedAt} />;
  const templates =
    withTemplates && !readOnly ? (
      <NoteTemplatesMenu
        notes={{ ...Object.fromEntries(Object.entries(encounter.notes ?? {}).filter(([, value]) => typeof value === 'string')), ...values } as Record<string, string>}
        onApply={(next) => {
          void write(next)
            .then(() => {
              setValues(Object.fromEntries(fields.map((field) => [field.key, next[field.key] ?? ''])));
              setDirty(false);
              setSavedAt(new Date());
            })
            .catch((error) => toast.error(tr('opd', 'the_note_could_not_be_saved'), describeError(error)));
        }}
      />
    ) : null;

  return (
    <EmrPanel
      icon={icon}
      title={title}
      description={description}
      actions={
        <>
          {templates}
          {status}
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map((field) => (
          <div key={field.key} className={field.wide ? 'sm:col-span-2' : undefined}>
            <Field label={field.label}>
              <Textarea
                rows={field.rows}
                readOnly={readOnly}
                value={values[field.key] ?? ''}
                onChange={(event) => {
                  const text = event.target.value;
                  setValues((current) => ({ ...current, [field.key]: text }));
                  setDirty(true);
                }}
              />
            </Field>
            {!readOnly && isQuickPickField(field.key) && pickers[field.key] && (
              <QuickPickChips field={field.key} learned={picks.learned[field.key]} department={picks.department[field.key]} onPick={pickers[field.key]} className="mt-2" />
            )}
          </div>
        ))}
      </div>
    </EmrPanel>
  );
}
